import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/usePageMeta';
import { api } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { useSettings } from '../lib/useSettings';
import { RoomCard } from '../components/RoomCard';
import { SingleRoom } from '../components/SingleRoom';
import { Notice } from '../components/Section';
import { Photo } from '../components/Mock';
import { HomeSections } from './home/HomeSections';
import { stayDate } from '../lib/format';
import { SEO } from '../seo/pages';

function eventDates(start: string | null, end: string | null): string | null {
  if (!start) return null;
  return end && end !== start ? `${stayDate(start)} to ${stayDate(end)}` : stayDate(start);
}

export function HomePage() {
  usePageMeta(SEO.home.title, SEO.home.description, { jsonLd: SEO.home.jsonLd });
  const { data: s } = useSettings();
  const rooms = useQuery({
    queryKey: ['rooms', 'teaser'],
    queryFn: ({ signal }) => api.rooms.list(null, signal),
    staleTime: 30_000,
  });
  const teaser = rooms.data?.rooms.slice(0, 3) ?? [];
  const when = eventDates(s?.eventStartDate ?? null, s?.eventEndDate ?? null);

  return (
    <>
      {/* Hero: Edem layout, yellow date, pink label, giant headline, blue CTA. */}
      <section className="bg-stage bg-hex relative overflow-hidden">
        <Photo
          name="hero.jpg"
          alt=""
          className="absolute inset-0 !bg-transparent opacity-60 [&>span]:hidden"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-night-950 via-night-950/70 to-transparent"
          aria-hidden
        />
        <span
          className="pointer-events-none absolute -right-24 top-24 hidden h-[520px] w-[520px] rotate-12 border-[14px] border-pink-500/80 lg:block"
          style={{ clipPath: 'polygon(50% 0, 100% 100%, 0 100%)' }}
          aria-hidden
        />
        {/*
          The hero owns the first screen: exactly one viewport tall (small
          viewport units so mobile browser chrome does not push it), content
          centred, nothing below it visible until the visitor scrolls. Short
          viewports get tighter padding and a headline capped by height so the
          buttons stay inside the fold.
        */}
        <div className="relative mx-auto flex min-h-svh max-w-6xl flex-col justify-center px-4 pb-16 pt-32 sm:pb-20 sm:pt-40 [@media(max-height:800px)]:pb-2 [@media(max-height:800px)]:pt-32 lg:[@media(max-height:800px)]:pt-24">
          <p className="text-meta text-xl sm:text-2xl [@media(max-height:800px)]:text-base">
            {when ?? 'Date coming soon'}
          </p>
          <p className="mt-3 [@media(max-height:800px)]:mt-2">
            <span className="label-highlight text-2xl sm:text-3xl [@media(max-height:800px)]:text-xl">
              Celebration of Life
            </span>
          </p>
          <h1 className="mt-6 text-[clamp(40px,10vh,60px)] leading-none sm:text-[clamp(48px,12vh,96px)] lg:text-[clamp(56px,12vh,110px)] [@media(max-height:800px)]:mt-2">
            Brandon
            <br />
            Taylor
          </h1>
          {/* The hashtag keeps its casing: text-meta uppercases, so normal-case wins here. */}
          <p className="text-meta mt-3 text-xl normal-case sm:text-2xl [@media(max-height:800px)]:mt-1 [@media(max-height:800px)]:text-base">
            #BrandonStrong
          </p>
          <div className="mt-6 max-w-xl [@media(max-height:800px)]:mt-4">
            <p className="text-lg text-ink-50 [@media(max-height:800px)]:text-base">
              One night, one hotel, everyone who loves Brandon
              {s?.hotelName ? ` at ${s.hotelName}` : ''}. The family is holding 62 rooms so nobody
              has to drive home early. Reserve yours, come Saturday for the welcome night, stay for
              the Sunday farewell, and dress like a rockstar.
            </p>
            <p className="mt-3 text-base text-ink-200 [@media(max-height:800px)]:text-sm">
              Not staying at the hotel? Please RSVP with the names of your party. A donation is
              welcome, never required, and all of it goes to Shawna.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4 [@media(max-height:800px)]:mt-4">
            <Link to="/rooms" className="btn-primary">
              Reserve a room
            </Link>
            <Link to="/rsvp" className="btn-outline">
              RSVP
            </Link>
            {s?.donationUrl ? (
              <a
                href={s.donationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent"
                onClick={() => trackEvent('donate_click')}
              >
                Donate to the family
              </a>
            ) : (
              <Link to="/donate" className="btn-accent">
                Donate to the family
              </Link>
            )}
          </div>
        </div>
      </section>

      {rooms.data?.allSoldOut && (
        <div className="mx-auto max-w-6xl px-4 pt-10">
          <Notice tone="danger">
            {s?.soldOutMessage ?? 'All rooms are spoken for. Thank you for the incredible support.'}
          </Notice>
        </div>
      )}

      {teaser.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-20">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="text-4xl sm:text-5xl">Rooms</h2>
            <Link to="/rooms" className="text-meta text-base hover:text-ink-50">
              All rooms →
            </Link>
          </div>
          {teaser.length === 1 ? (
            <SingleRoom room={teaser[0]!} settings={s} query="" />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {teaser.map((room) => (
                <RoomCard key={room.id} room={room} query="" />
              ))}
            </div>
          )}
        </section>
      )}

      <HomeSections s={s} />
    </>
  );
}
